/**
 * VariablesEditor — 模板变量定义编辑器（列表式，避免手写 JSON 出错）
 * 每个变量两列：变量名 + 说明。支持添加/删除/导入 JSON /一键插入占位符。
 */
import { useState } from 'react'
import {
  makeStyles,
  tokens,
  Input,
  Button,
  Badge,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogContent,
  DialogActions,
  Textarea,
  Field,
  InfoLabel,
} from '@fluentui/react-components'
import { AddRegular, DeleteRegular, ArrowImportRegular } from '@fluentui/react-icons'
import { DialogCloseTitle } from '@/components/DialogCloseTitle'
import type { TemplateVariable } from '@/api/types'

interface Props {
  items: TemplateVariable[]
  onChange: (items: TemplateVariable[]) => void
}

const useStyles = makeStyles({
  wrap: { display: 'flex', flexDirection: 'column', gap: '8px' },
  row: { display: 'flex', gap: '8px', alignItems: 'center' },
  nameInput: { flex: '0 0 200px', minWidth: '140px' },
  descInput: { flex: 1, minWidth: '160px' },
  hint: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    alignItems: 'center',
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },
  chip: {
    cursor: 'pointer',
    ':hover': { opacity: 0.8 },
  },
  toolbar: { display: 'flex', gap: '8px', alignItems: 'center' },
})

/** 解析导入文本 → TemplateVariable[]（支持 JSON 数组 / 每行 "name=desc" 或 "name desc"） */
export function parseVariablesImport(raw: string): TemplateVariable[] {
  const trimmed = raw.trim()
  if (!trimmed) return []
  // JSON 数组：[{"name":"code","desc":"验证码"}] 或 [{"name":"code"}]
  try {
    const arr = JSON.parse(trimmed)
    if (Array.isArray(arr)) {
      return arr
        .map((v) => ({
          name: typeof v?.name === 'string' ? v.name : '',
          desc: typeof v?.desc === 'string' ? v.desc : '',
        }))
        .filter((v) => v.name)
    }
  } catch {
    /* fall through */
  }
  // 逐行 "name=desc" 或 "name desc"
  const out: TemplateVariable[] = []
  for (const line of trimmed.split(/\r?\n/)) {
    const item = line.trim()
    if (!item) continue
    const idx = item.indexOf('=')
    if (idx > 0) {
      out.push({ name: item.slice(0, idx).trim(), desc: item.slice(idx + 1).trim() })
    } else {
      const sp = item.search(/\s/)
      if (sp > 0) out.push({ name: item.slice(0, sp).trim(), desc: item.slice(sp + 1).trim() })
      else out.push({ name: item, desc: '' })
    }
  }
  return out
}

export default function VariablesEditor({ items, onChange }: Props) {
  const styles = useStyles()
  const [importOpen, setImportOpen] = useState(false)
  const [importText, setImportText] = useState('')

  const addRow = () => onChange([...items, { name: '', desc: '' }])
  const updateRow = (i: number, patch: Partial<TemplateVariable>) =>
    onChange(items.map((it, j) => (j === i ? { ...it, ...patch } : it)))
  const removeRow = (i: number) => onChange(items.filter((_, j) => j !== i))

  const applyImport = () => {
    const parsed = parseVariablesImport(importText)
    if (parsed.length > 0) onChange([...items, ...parsed])
    setImportOpen(false)
    setImportText('')
  }

  return (
    <div className={styles.wrap}>
      {items.map((it, i) => (
        <div key={i} className={styles.row}>
          <Input
            className={styles.nameInput}
            placeholder="变量名，如 code"
            value={it.name}
            onChange={(_, d) => updateRow(i, { name: d.value })}
          />
          <Input
            className={styles.descInput}
            placeholder="说明（可选），如 验证码"
            value={it.desc ?? ''}
            onChange={(_, d) => updateRow(i, { desc: d.value })}
          />
          <Button appearance="subtle" size="small" icon={<DeleteRegular />} aria-label="删除" onClick={() => removeRow(i)} />
        </div>
      ))}

      <div className={styles.toolbar}>
        <Button appearance="subtle" size="small" icon={<AddRegular />} onClick={addRow}>
          添加变量
        </Button>
        <Button appearance="subtle" size="small" icon={<ArrowImportRegular />} onClick={() => { setImportText(''); setImportOpen(true) }}>
          导入
        </Button>
      </div>

      <div className={styles.hint}>
        <span>占位符写法：</span>
        {items.map((it) => it.name).filter(Boolean).map((n) => (
          <Badge
            key={n}
            className={styles.chip}
            size="small"
            appearance="outline"
            color="informative"
            title="点击复制占位符"
            onClick={() => navigator.clipboard?.writeText(`{{${n}}}`)}
          >
            {'{{' + n + '}}'}
          </Badge>
        ))}
        {items.filter((i) => i.name).length === 0 && <span>（添加变量后，可点击占位符复制）</span>}
      </div>

      <Dialog open={importOpen} onOpenChange={(_, d) => !d.open && setImportOpen(false)} modalType="alert">
        <DialogSurface>
          <DialogBody>
            <DialogCloseTitle title="导入变量定义" onClose={() => setImportOpen(false)} />
            <DialogContent>
              <Field
                label={
                  <InfoLabel info="支持 JSON 数组，如 [{&quot;name&quot;:&quot;code&quot;,&quot;desc&quot;:&quot;验证码&quot;}]；或每行 “name=说明”。导入后追加到当前列表。">
                    粘贴内容
                  </InfoLabel>
                }
              >
                <Textarea
                  rows={6}
                  placeholder={'code=验证码\nname=收件人姓名'}
                  value={importText}
                  onChange={(_, d) => setImportText(d.value)}
                />
              </Field>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setImportOpen(false)}>取消</Button>
              <Button appearance="primary" onClick={applyImport}>导入</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  )
}

/** 空行清理：过滤掉 name 为空的项 */
export function cleanVariables(items: TemplateVariable[]): TemplateVariable[] {
  return items.filter((it) => it.name.trim())
}